import api from './api';

class TenantService {
  
  /**
   * Récupère les informations de configuration du tenant (authentification requise)
   */
  async getConfig() {
    try {
      const response = await api.get('/api/tenant/config');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération de la config tenant:', error);
      throw error;
    }
  }

  /**
   * Récupère uniquement le branding (publique)
   */
  async getBranding() {
    try {
      const response = await api.get('/api/tenant/branding');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du branding:', error);
      throw error;
    }
  }

  /**
   * Met à jour la configuration du tenant
   */
  async updateConfig(configData) {
    try {
      const response = await api.put('/api/tenant/config', configData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la config tenant:', error);
      throw error;
    }
  }

  /**
   * Upload du logo de l'établissement
   */
  async uploadLogo(logoFile) {
    try {
      // Validation côté client
      if (!logoFile) {
        throw new Error('Aucun fichier logo fourni');
      }
      
      // Vérifier le type de fichier
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(logoFile.type)) {
        throw new Error('Format de fichier non supporté. Utilisez JPG, PNG, GIF ou WebP.');
      }
      
      // Vérifier la taille (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (logoFile.size > maxSize) {
        throw new Error('Le fichier est trop volumineux. Taille maximum : 5MB.');
      }
      
      const formData = new FormData();
      formData.append('logo', logoFile);
      
      console.log('Upload du logo:', {
        nom: logoFile.name,
        taille: logoFile.size,
        type: logoFile.type
      });
      
      const response = await api.post('/api/tenant/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Réponse upload logo:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'upload du logo:', error);
      
      // Améliorer le message d'erreur
      if (error.response?.data) {
        const backendError = error.response.data;
        throw new Error(backendError.details || backendError.error || 'Erreur lors de l\'upload du logo');
      }
      
      throw error;
    }
  }

  /**
   * Upload du favicon de l'établissement
   */
  async uploadFavicon(faviconFile) {
    try {
      // Validation côté client
      if (!faviconFile) {
        throw new Error('Aucun fichier favicon fourni');
      }
      
      // Vérifier le type de fichier
      const allowedTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!allowedTypes.includes(faviconFile.type) && !faviconFile.name.endsWith('.ico')) {
        throw new Error('Format de fichier non supporté. Utilisez ICO, PNG, JPG ou SVG.');
      }
      
      // Vérifier la taille (max 1MB pour les favicons)
      const maxSize = 1 * 1024 * 1024; // 1MB
      if (faviconFile.size > maxSize) {
        throw new Error('Le fichier est trop volumineux. Taille maximum pour un favicon : 1MB.');
      }
      
      const formData = new FormData();
      formData.append('favicon', faviconFile);
      
      console.log('Upload du favicon:', {
        nom: faviconFile.name,
        taille: faviconFile.size,
        type: faviconFile.type
      });
      
      const response = await api.post('/api/tenant/upload-favicon', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Réponse upload favicon:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'upload du favicon:', error);
      
      // Améliorer le message d'erreur
      if (error.response?.data) {
        const backendError = error.response.data;
        throw new Error(backendError.details || backendError.error || 'Erreur lors de l\'upload du favicon');
      }
      
      throw error;
    }
  }

  /**
   * Upload de l'image de fond pour la page de connexion
   */
  async uploadBackground(backgroundFile) {
    try {
      // Validation côté client
      if (!backgroundFile) {
        throw new Error('Aucun fichier d\'image de fond fourni');
      }
      
      // Vérifier le type de fichier
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(backgroundFile.type)) {
        throw new Error('Format de fichier non supporté. Utilisez JPG, PNG ou WebP.');
      }
      
      // Vérifier la taille (max 10MB pour les images de fond)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (backgroundFile.size > maxSize) {
        throw new Error('Le fichier est trop volumineux. Taille maximum : 10MB.');
      }
      
      const formData = new FormData();
      formData.append('background', backgroundFile);
      
      console.log('Upload de l\'image de fond:', {
        nom: backgroundFile.name,
        taille: backgroundFile.size,
        type: backgroundFile.type
      });
      
      const response = await api.post('/api/tenant/upload-background', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Réponse upload image de fond:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'upload de l\'image de fond:', error);
      
      // Améliorer le message d'erreur
      if (error.response?.data) {
        const backendError = error.response.data;
        throw new Error(backendError.details || backendError.error || 'Erreur lors de l\'upload de l\'image de fond');
      }
      
      throw error;
    }
  }
}

export default new TenantService();
